import { CIListFilter, DatePicker, MultiSelectDropdown, getLocaleDateFormat } from 'ccs-ui-components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FILTER, REG_STATUS_TYPES, STATUS_LIST } from '../../constants';
import { convertDateToYYMMDDFormat } from '../../utilities/common/DateUtils';
import { ISourceListQueryParams } from '../../modules/sources/source-registry/source-list/SourceList';

interface IRegistries {
  clearFunction: () => void;
  queryParams: ISourceListQueryParams;
  setQueryParams: Dispatch<SetStateAction<ISourceListQueryParams>>
}

const RegistriesFilter = ({ clearFunction, setQueryParams, queryParams }: IRegistries) => {
  const { t: translate } = useTranslation();
  const [fromDate, setFromDate] = useState<Date | null>(queryParams.fromDate ? new Date(queryParams.fromDate) : null);
  const [toDate, setToDate] = useState<Date | null>(queryParams.toDate ? new Date(queryParams.toDate) : null);

  const [statusMultiFilterValue, setStatusMultiFilterValue] = useState<string[]>(queryParams.status ? queryParams.status.split(',') : STATUS_LIST);
  const [selectAllOptions, setSelectAllOptions] = useState<boolean>(false);

  const checkApplyStatus = () => {
    const selectedStatus = statusMultiFilterValue.length === REG_STATUS_TYPES.length ? '' : statusMultiFilterValue.join(',');
    const prevStatus = queryParams.status ?? '';
    const statusChanged = selectedStatus !== prevStatus;

    if (
      (fromDate && (new Date(queryParams.fromDate as string).getTime() !== fromDate?.getTime()))
      || (toDate && (new Date(queryParams.toDate as string).getTime() !== toDate?.getTime()))
      || statusChanged
    ) return true;
    return false;
  };
  // on Click of Apply Button
  const onApplyHandler = () => {
    setQueryParams((pre) => ({
      ...pre,
      toDate: toDate !== null ? `${convertDateToYYMMDDFormat(toDate)}${FILTER.TO_TIMESTAMP_EXTENSION_STR}` : '',
      fromDate: fromDate !== null ? `${convertDateToYYMMDDFormat(fromDate)}${FILTER.FROM_TIMESTAMP_EXTENSION_STR}` : '',
      status: statusMultiFilterValue.length === REG_STATUS_TYPES.length ? '' : statusMultiFilterValue.join(','),
      page: ''
    }));
  };
  // on Click of Clear Button
  const onClearHandler = () => {
    setFromDate(null);
    setToDate(null);
    clearFunction();
  };
  // setting Values from Query Params
  const setFilterStates = () => {
    setFromDate(queryParams.fromDate ? new Date(queryParams.fromDate) : null);
    setToDate(queryParams.toDate ? new Date(queryParams.toDate) : null);
    setStatusMultiFilterValue(queryParams.status ? queryParams.status.split(',') : STATUS_LIST);
    setSelectAllOptions((queryParams.status === '' || queryParams.status === undefined) ? true : queryParams?.status?.split(',').length >= 2);
  };
  // setting query Params in url
  useEffect(() => {
    if (Object.keys(queryParams).length !== 0) {
      setFilterStates();
    }
  }, [queryParams]);

  return (
    <CIListFilter applyEnabled={checkApplyStatus()} onClear={onClearHandler} onApply={onApplyHandler}>
      <Row>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('source-topic-registry-filter.txt_last_modified_start'))}
            selected={fromDate}
            onChange={(date: Date | null) => setFromDate(date)}
            maxDate={toDate ?? new Date()}
            placeholderText={String(translate('source-topic-registry-filter.txt_date_range'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterStartDate"
          />
        </Col>
        <Col xs={12} md={6}>
          <DatePicker
            labelText={String(translate('source-topic-registry-filter.txt_last_modified_end'))}
            selected={toDate}
            onChange={(date: Date | null) => setToDate(date)}
            maxDate={new Date()}
            minDate={fromDate ?? undefined}
            placeholderText={String(translate('source-topic-registry-filter.txt_date_range'))}
            dateFormat={getLocaleDateFormat()}
            data-testid="ciListFilterEndDate"
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <MultiSelectDropdown
            labelText={String(translate('source-topic-registry-filter.txt_status'))}
            options={REG_STATUS_TYPES}
            selectedOptions={statusMultiFilterValue}
            onSelect={(e: string[]) => setStatusMultiFilterValue(e)}
            selectAll={selectAllOptions}
            handleSelectAll={(e: boolean) => setSelectAllOptions(e)}
            height="38px"
          />
        </Col>
      </Row>
    </CIListFilter>
  );
};

export default RegistriesFilter;
